/**
 * Custom hooks for venture actions with cache invalidation and notifications
 * Provides high-level mutation operations with optimistic updates
 *
 * Requirements:
 * - 1.1, 1.2: Venture creation
 * - 1.3: Venture editing
 * - 1.4: Venture deletion
 * - 1.5: Venture publishing
 * - 2.2: Status management
 * - 3.1, 3.5: Authorization and validation
 */
import { mutate } from "swr";
import { useCreateVenture, useUpdateVenture, useDeleteVenture, usePublishVenture } from "../api/ventures";
/**
 * Helper function to invalidate ventures cache
 * Invalidates all venture-related cache entries for a workspace
 *
 * @param workspaceId - The workspace ID to invalidate cache for
 */
export function invalidateVenturesCache(workspaceId) {
    // Invalidate workspace ventures list
    mutate((key) => {
        if (typeof key !== "string")
            return false;
        try {
            const parsedKey = JSON.parse(key);
            if (!Array.isArray(parsedKey))
                return false;
            // Match workspace ventures endpoint
            if (parsedKey[0] === `/api/ventures/workspace/${workspaceId}`) {
                return true;
            }
            // Match user's ventures endpoint
            if (parsedKey[0] === "/api/users/me/ventures") {
                return true;
            }
            return false;
        }
        catch {
            return false;
        }
    }, undefined, { revalidate: true });
    // Also invalidate public ventures cache if needed
    mutate((key) => {
        if (typeof key !== "string")
            return false;
        try {
            const parsedKey = JSON.parse(key);
            if (!Array.isArray(parsedKey))
                return false;
            // Match public ventures endpoints
            return parsedKey[0] === "/api/public/ventures" || parsedKey[0] === "/api/public/ventures/featured";
        }
        catch {
            return false;
        }
    }, undefined, { revalidate: true });
}
/**
 * Helper function to invalidate a specific venture's cache
 *
 * @param ventureId - The venture ID to invalidate cache for
 */
export function invalidateVentureCache(ventureId) {
    mutate(`/api/ventures/${ventureId}`, undefined, { revalidate: true });
}
/**
 * Hook for venture mutation actions
 * Provides create, update, delete, and publish operations with cache management
 *
 * @param workspaceId - The workspace ID for venture operations
 * @param notify - Optional notification adapter for success/error messages
 * @returns Object with mutation functions and loading states
 *
 * @example
 * ```tsx
 * const { createVenture, isCreating } = useVentureActions(workspaceId, {
 *   success: (msg) => toast.success(msg),
 *   error: (msg) => toast.error(msg),
 * });
 *
 * await createVenture({
 *   name: "My Venture",
 *   type: "real_estate",
 *   pitchContent: "...",
 *   targetGoal: 50000,
 * });
 * ```
 */
export function useVentureActions(workspaceId, notify) {
    const createVentureMutation = useCreateVenture(workspaceId);
    const deleteVentureMutation = useDeleteVenture(workspaceId);
    /**
     * Create a new venture
     * Requirement 1.1, 1.2: Venture creation with validation
     */
    const createVenture = async (data) => {
        try {
            const result = await createVentureMutation.trigger(data);
            // Invalidate cache after successful creation
            invalidateVenturesCache(workspaceId);
            // Show success notification
            notify?.success("Venture created successfully");
            return { success: true, data: result };
        }
        catch (error) {
            const message = error instanceof Error ? error.message : "Failed to create venture";
            // Show error notification
            notify?.error(message);
            return {
                success: false,
                error: message,
            };
        }
    };
    /**
     * Delete a venture
     * Requirement 1.4: Venture deletion with authorization
     */
    const deleteVenture = async (ventureId) => {
        try {
            await deleteVentureMutation.trigger(ventureId);
            // Invalidate cache after successful deletion
            invalidateVenturesCache(workspaceId);
            invalidateVentureCache(ventureId);
            // Show success notification
            notify?.success("Venture deleted successfully");
            return { success: true };
        }
        catch (error) {
            const message = error instanceof Error ? error.message : "Failed to delete venture";
            // Show error notification
            notify?.error(message);
            return {
                success: false,
                error: message,
            };
        }
    };
    return {
        createVenture,
        deleteVenture,
        isCreating: createVentureMutation.isLoading,
        isDeleting: deleteVentureMutation.isLoading,
    };
}
/**
 * Hook for updating a specific venture
 * Must be used at component level with a specific venture ID
 * Requirement 1.3, 3.1, 3.2: Venture editing with authorization
 *
 * @param ventureId - The venture ID to update
 * @param workspaceId - The workspace ID for authorization
 * @param notify - Optional notification adapter for success/error messages
 * @returns Object with update function and loading state
 *
 * @example
 * ```tsx
 * const { updateVenture, isUpdating } = useVentureUpdate(ventureId, workspaceId, {
 *   success: (msg) => toast.success(msg),
 *   error: (msg) => toast.error(msg),
 * });
 *
 * await updateVenture({
 *   name: "Updated Name",
 *   pitchContent: "Updated pitch...",
 * });
 * ```
 */
export function useVentureUpdate(ventureId, workspaceId, notify) {
    const updateVentureMutation = useUpdateVenture(ventureId, workspaceId);
    /**
     * Update the venture
     * Requirement 1.3, 3.2: Venture editing
     */
    const updateVenture = async (data) => {
        try {
            const result = await updateVentureMutation.trigger(data);
            // Invalidate cache after successful update
            invalidateVenturesCache(workspaceId);
            invalidateVentureCache(ventureId);
            // Show success notification
            notify?.success("Venture updated successfully");
            return { success: true, data: result };
        }
        catch (error) {
            const message = error instanceof Error ? error.message : "Failed to update venture";
            // Show error notification
            notify?.error(message);
            return {
                success: false,
                error: message,
            };
        }
    };
    return {
        updateVenture,
        isUpdating: updateVentureMutation.isLoading,
    };
}
/**
 * Hook for publishing a venture
 * Transitions venture status from 'draft' to 'live'
 * Requirement 2.2, 3.5: Status management and publishing
 *
 * @param ventureId - The venture ID to publish
 * @param workspaceId - The workspace ID for authorization
 * @param notify - Optional notification adapter for success/error messages
 * @returns Object with publish function and loading state
 *
 * @example
 * ```tsx
 * const { publishVenture, isPublishing } = useVenturePublish(ventureId, workspaceId, {
 *   success: (msg) => toast.success(msg),
 *   error: (msg) => toast.error(msg),
 * });
 *
 * await publishVenture();
 * ```
 */
export function useVenturePublish(ventureId, workspaceId, notify) {
    const publishVentureMutation = usePublishVenture(ventureId, workspaceId);
    /**
     * Publish the venture
     * Requirement 2.2: Status transition to 'live'
     */
    const publishVenture = async () => {
        try {
            const result = await publishVentureMutation.trigger(undefined);
            // Invalidate cache after successful publish
            invalidateVenturesCache(workspaceId);
            invalidateVentureCache(ventureId);
            // Show success notification
            notify?.success("Venture published successfully");
            return { success: true, data: result };
        }
        catch (error) {
            const message = error instanceof Error ? error.message : "Failed to publish venture";
            // Show error notification
            notify?.error(message);
            return {
                success: false,
                error: message,
            };
        }
    };
    return {
        publishVenture,
        isPublishing: publishVentureMutation.isLoading,
    };
}
